import { on } from '@ember/modifier';
import { action, set } from '@ember/object';
import didInsert from '@ember/render-modifiers/modifiers/did-insert'; // eslint-disable-line ember/no-at-ember-render-modifiers
import willDestroy from '@ember/render-modifiers/modifiers/will-destroy'; // eslint-disable-line ember/no-at-ember-render-modifiers
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import t from 'ember-intl/helpers/t';
import { or } from 'ember-truth-helpers';
import IMask from 'imask';
import type SignInField from 'invite-sign/models/sign-in-field';
import {
  DateFieldFormat,
  convertFromISO,
  convertToISO,
  getDateMaskConfig,
  isValidDate,
} from 'invite-sign/utils/date-format';

interface DateFieldSignature {
  Args: {
    id: string;
    mappedField: SignInField;
  };
}

const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export default class DateField extends Component<DateFieldSignature> {
  @tracked displayValue: string = '';
  @tracked isValid = false;
  @tracked rejectionError = '';
  @tracked partialError = '';

  imaskInstance?: ReturnType<typeof IMask>;
  private errorTimeout?: ReturnType<typeof setTimeout>;

  get format(): DateFieldFormat {
    return (this.args.mappedField.dateFormat as DateFieldFormat) || DateFieldFormat.MM_DD_YYYY;
  }

  get maskConfig() {
    return getDateMaskConfig(this.format);
  }

  get isComplete(): boolean {
    return !this.displayValue.includes('_') && this.displayValue.length > 0;
  }

  get showValidationError(): boolean {
    if (this.rejectionError) return true;
    if (this.partialError) return true;
    if (this.isComplete && !this.isValid) return true;
    return false;
  }

  get errorMessage(): string {
    if (this.rejectionError) return this.rejectionError;
    if (this.partialError) return this.partialError;
    if (this.isComplete && !this.isValid) return 'Invalid date';
    return '';
  }

  get inputClasses(): string {
    const hasError = this.showValidationError || this.hasModelError;
    const errorClass = hasError ? 'error border-red-500' : '';
    return `inputClass rounded-md ${errorClass}`.trim();
  }

  get hasModelError(): boolean {
    return !isEmpty(this.args.mappedField?.errors);
  }

  get modelErrorKey(): string {
    const messages = this.args.mappedField?.errors?.messages as unknown as string[];
    return messages?.[0] ?? '';
  }

  constructor(owner: unknown, args: DateFieldSignature['Args']) {
    super(owner, args);
    const existingValue = this.args.mappedField.value;
    if (existingValue && /^\d{4}-\d{2}-\d{2}$/.test(existingValue)) {
      this.displayValue = convertFromISO(existingValue, this.format) ?? existingValue;
      const parts = existingValue.split('-').map(Number) as [number, number, number];
      this.isValid = isValidDate(parts[2], parts[1], parts[0]);
    } else if (existingValue) {
      this.displayValue = existingValue;
    }
  }

  private validateDate(value: string): boolean {
    const parsed = this.maskConfig.parse(value);
    if (!parsed) return false;
    return isValidDate(parsed.day, parsed.month, parsed.year);
  }

  private extractPartialDate(value: string): { month?: number; day?: number; year?: number } {
    const mask = this.maskConfig.mask;
    const result: { month?: number; day?: number; year?: number } = {};

    const blockRegex = /MM|DD|YYYY/g;
    let match;

    while ((match = blockRegex.exec(mask)) !== null) {
      const blockName = match[0];
      const startPos = match.index;
      const length = blockName.length;
      const valuePart = value.substring(startPos, startPos + length);

      if (!valuePart || valuePart.length < length || valuePart.includes('_')) {
        continue;
      }

      const num = parseInt(valuePart, 10);
      if (isNaN(num)) continue;

      if (blockName === 'MM') result.month = num;
      else if (blockName === 'DD') result.day = num;
      else if (blockName === 'YYYY') result.year = num;
    }

    return result;
  }

  private validatePartialDate(value: string): string | null {
    const { month, day, year } = this.extractPartialDate(value);

    if (month !== undefined && day !== undefined) {
      const maxDays = DAYS_IN_MONTH[month - 1];

      if (maxDays === undefined) {
        return null;
      }

      if (month === 2 && year !== undefined) {
        const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
        const febDays = isLeapYear ? 29 : 28;

        if (day > febDays) {
          return isLeapYear ? 'February has max 29 days in leap year' : `February has max 28 days in ${year}`;
        }
      } else if (month === 2 && day > 29) {
        return 'February has max 29 days';
      } else if (day > maxDays) {
        return `${MONTH_NAMES[month - 1]} has max ${maxDays} days`;
      }
    }

    return null;
  }

  private clearErrorTimeout(): void {
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
      this.errorTimeout = undefined;
    }
  }

  private showTemporaryError(message: string): void {
    this.clearErrorTimeout();
    this.rejectionError = message;

    this.errorTimeout = setTimeout(() => {
      this.rejectionError = '';
    }, 2000);
  }

  @action
  setupIMask(element: HTMLInputElement): void {
    const config = this.maskConfig;
    this.imaskInstance = IMask(element, {
      mask: config.mask,
      blocks: config.blocks,
      lazy: false,
      placeholderChar: '_',
    });

    if (this.displayValue) {
      this.imaskInstance.value = this.displayValue;
    }

    this.imaskInstance.on('accept', () => {
      this.clearErrorTimeout();
      this.rejectionError = '';

      const currentValue = this.imaskInstance?.value || '';
      this.displayValue = currentValue;
      this.isValid = this.validateDate(currentValue);

      const partialValidationError = this.validatePartialDate(currentValue);
      this.partialError = partialValidationError ?? '';

      if (this.isValid) {
        const iso = convertToISO(currentValue, this.format);
        if (iso) {
          set(this.args.mappedField, 'value', iso);
        }
      } else {
        const hasUserInput = /\d/.test(currentValue);
        set(this.args.mappedField, 'value', hasUserInput ? currentValue : '');
      }

      this.args.mappedField.validate();
    });

    const handleBeforeInput = (e: InputEvent) => {
      if (e.data && /\d/.test(e.data)) {
        const valueBefore = this.imaskInstance?.value ?? '';

        setTimeout(() => {
          const valueAfter = this.imaskInstance?.value ?? '';

          if (valueBefore === valueAfter && e.data) {
            const cursorPos = element.selectionStart ?? 0;
            const maskFormat = config.mask;

            if (maskFormat === 'MM/DD/YYYY') {
              if (cursorPos <= 2) {
                this.showTemporaryError('Month must be 01-12');
              } else if (cursorPos <= 5) {
                this.showTemporaryError('Day must be 01-31');
              } else {
                this.showTemporaryError('Year must be 1900-2100');
              }
            } else if (maskFormat === 'DD/MM/YYYY') {
              if (cursorPos <= 2) {
                this.showTemporaryError('Day must be 01-31');
              } else if (cursorPos <= 5) {
                this.showTemporaryError('Month must be 01-12');
              } else {
                this.showTemporaryError('Year must be 1900-2100');
              }
            } else if (maskFormat === 'YYYY-MM-DD') {
              if (cursorPos <= 4) {
                this.showTemporaryError('Year must be 1900-2100');
              } else if (cursorPos <= 7) {
                this.showTemporaryError('Month must be 01-12');
              } else {
                this.showTemporaryError('Day must be 01-31');
              }
            } else if (maskFormat === 'DD.MM.YYYY') {
              if (cursorPos <= 2) {
                this.showTemporaryError('Day must be 01-31');
              } else if (cursorPos <= 5) {
                this.showTemporaryError('Month must be 01-12');
              } else {
                this.showTemporaryError('Year must be 1900-2100');
              }
            }
          }
        }, 0);
      }
    };

    const handleInput = () => {
      setTimeout(() => {
        const currentValue = this.imaskInstance?.value ?? '';
        if (currentValue !== this.displayValue) {
          this.clearErrorTimeout();
          this.rejectionError = '';

          this.displayValue = currentValue;
          this.isValid = this.validateDate(currentValue);
          const partialValidationError = this.validatePartialDate(currentValue);
          this.partialError = partialValidationError ?? '';
        }
      }, 0);
    };

    const handleKeyDown = () => {
      this.clearErrorTimeout();
      this.rejectionError = '';
    };

    element.addEventListener('keydown', handleKeyDown);
    element.addEventListener('beforeinput', handleBeforeInput);
    element.addEventListener('input', handleInput);
  }

  @action
  handleBlur(): void {
    this.args.mappedField.validate();
  }

  @action
  teardownIMask(): void {
    this.clearErrorTimeout();
    this.imaskInstance?.destroy();
    this.imaskInstance = undefined;
  }

  <template>
    <div data-test-date-field>
      <span class="text-gray-500 text-xs" data-test-date-format-hint>({{this.format}})</span>
      <input
        data-test-date-input
        id={{@id}}
        type="text"
        inputmode="numeric"
        class={{this.inputClasses}}
        name={{@mappedField.identifier}}
        disabled={{@mappedField.isDisabled}}
        autocomplete="off"
        aria-invalid={{if (or this.showValidationError this.hasModelError) "true"}}
        data-lpignore="true"
        data-1p-ignore="true"
        data-form-type="other"
        data-test-date-invalid={{if (or this.showValidationError this.hasModelError) "true"}}
        {{didInsert this.setupIMask}}
        {{willDestroy this.teardownIMask}}
        {{on "blur" this.handleBlur}}
      />
      {{#if this.showValidationError}}
        <div class="bottom-error pt-1" data-test-date-error>
          {{this.errorMessage}}
        </div>
      {{else if this.hasModelError}}
        <div class="bottom-error pt-1" data-test-date-error>
          {{t this.modelErrorKey}}
        </div>
      {{/if}}
    </div>
  </template>
}
